// Canonical Markur Terms of Service — this file IS the source of truth.
// Referenced by utils/legal.ts (TERMS_URL + TERMS_VERSION) in markurhealth/app.
// When editing: bump the effective/updated date rendered below. If the change
// materially affects user rights, liability, or dispute resolution, also bump
// TERMS_VERSION in the app repo so new sign-ups stamp the new version.
import { createFileRoute, Link } from '@tanstack/react-router'
import { Logo } from '@/components/Logo'

export const Route = createFileRoute('/terms-of-service')({
  component: TermsOfService,
})

function TermsOfService() {
  return (
    <main className="bg-muted min-h-screen py-12">
      <header className="mx-auto mb-12 max-w-3xl px-8">
        <Link to="/" aria-label="Go to homepage">
          <Logo size="md" />
        </Link>
      </header>

      <article className="mx-auto max-w-3xl px-8">
        <h1 className="text-foreground mb-4 text-4xl font-bold">Terms of Service</h1>
        <p className="text-muted-foreground mb-12 text-sm">
          Effective date: 16 July 2026 &middot; Last updated: 19 August 2026
        </p>

        <div className="text-foreground space-y-10">
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">1. These terms</h2>
            <p>
              These Terms of Service ("<strong>Terms</strong>") are a legal agreement between you
              and <strong>Markur Health Limited</strong>, a company registered in Ireland (CRO
              company number 817464; registered office Apartment 610, Domville Crescent, Cherrywood,
              Dublin 18, D18 WRW7) ("<strong>Markur</strong>", "<strong>we</strong>", "
              <strong>us</strong>", "<strong>our</strong>").
            </p>
            <p>
              These Terms govern your use of the Markur mobile application and related services
              (together, the "<strong>App</strong>"). By creating an account or using the App, you
              agree to these Terms and to our{' '}
              <Link to="/privacy-policy" className="font-medium underline">
                Privacy Policy
              </Link>
              . If you do not agree, do not use the App.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">2. Important health notice</h2>
            <blockquote className="border-primary/60 bg-background/40 space-y-3 rounded-md border-l-4 p-4">
              <p>
                <strong>
                  Markur is a wellness application. It is not a medical device. It does not provide
                  medical advice, diagnosis, or treatment.
                </strong>
              </p>
              <p>
                Health scores, biomarker ranges, insights, and AI-chat responses are for
                informational and educational purposes only. They are{' '}
                <strong>
                  not a substitute for consultation with a licensed healthcare professional
                </strong>
                .
              </p>
              <p>
                <strong>Always seek the advice of a qualified clinician</strong> with any questions
                you have about your health, a medical condition, or test results.{' '}
                <strong>Never disregard professional medical advice or delay seeking it</strong>{' '}
                because of something you have read or seen in the App.
              </p>
              <p>
                <strong>
                  In an emergency, call your local emergency number or seek immediate medical
                  attention.
                </strong>{' '}
                Do not rely on the App in emergencies.
              </p>
            </blockquote>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">3. Who can use the App</h2>
            <p>You may use the App only if:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                You are at least <strong>16 years old</strong>.
              </li>
              <li>You have the legal capacity to enter into a binding contract.</li>
              <li>You are not barred from using the App under applicable law.</li>
            </ul>
            <p>
              The App is not intended for users under 16. If you are a parent or guardian and
              believe a child under 16 has created an account, please contact{' '}
              <strong>support@markurhealth.com</strong>.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">4. Your account</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>You create an account using Google Sign-In.</li>
              <li>You are responsible for the accuracy of the information you provide.</li>
              <li>
                You are responsible for keeping your Google account credentials secure. Any activity
                under your account is your responsibility.
              </li>
              <li>
                You must notify us promptly at <strong>support@markurhealth.com</strong> if you
                believe your account has been accessed without your permission.
              </li>
            </ul>
            <p>One account per person. You may not share your account with anyone else.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">5. Acceptable use</h2>
            <p>
              When using the App, you agree <strong>not</strong> to:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Upload or enter data that is not your own, unless you have explicit permission from
                the person it belongs to.
              </li>
              <li>
                Invite others to the Family feature or share health data without the explicit
                consent of the recipient.
              </li>
              <li>Use the App for any unlawful purpose or in violation of any applicable law.</li>
              <li>
                Attempt to access, scrape, or reverse-engineer any part of the App or our backend
                services.
              </li>
              <li>
                Upload malicious files, attempt to interfere with our services, or circumvent rate
                limits and security measures.
              </li>
              <li>
                Use the AI chat to generate content that is defamatory, harassing, illegal, or that
                infringes others' rights.
              </li>
            </ul>
            <p>We may suspend or terminate your access if you breach these rules.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">6. AI assistant</h2>
            <p>
              The App includes an AI chat assistant powered by Google's Gemini model, run via Google
              Vertex AI in the EU (multi-region).
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                The assistant{' '}
                <strong>may produce inaccurate, incomplete, or misleading information</strong>. You
                must not rely on its responses for medical decisions.
              </li>
              <li>
                Messages you send and a relevant summary of your biomarker data are{' '}
                <strong>transmitted to Google</strong> for inference. Do not share information you
                do not wish to transmit to Google.
              </li>
              <li>
                The AI assistant is a convenience feature; we do not guarantee its availability,
                accuracy, or continued operation in its current form.
              </li>
            </ul>
            <p>
              For more detail, see Section 5 of our{' '}
              <Link to="/privacy-policy" className="font-medium underline">
                Privacy Policy
              </Link>
              .
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">7. Lab-report uploads</h2>
            <p>
              When you upload a lab report (PDF or image) to the App, the file is temporarily stored
              in encrypted Supabase Storage (Ireland, EU) while it is transmitted to Google Vertex
              AI (Gemini) in the EU for automatic extraction of biomarker values. The file is
              permanently deleted from Supabase Storage within one hour of processing by an
              automated cleanup job; only the extracted values are saved to your account. Rate
              limits apply (currently 10 uploads per user per hour). See the{' '}
              <Link to="/privacy-policy" className="font-medium underline">
                Privacy Policy
              </Link>{' '}
              &sect;5 for full detail.
            </p>
            <p>
              You remain responsible for verifying that extracted values are accurate before relying
              on them.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">8. Family feature</h2>
            <p>
              The Family feature allows you to invite other Markur users to share biomarker data
              with you, and to share your own biomarker data with them, on a{' '}
              <strong>per-biomarker</strong> permission basis.
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                You control what you share, and you can change or revoke permissions at any time in
                the Family settings.
              </li>
              <li>
                You are responsible for ensuring that anyone you invite consents to receiving your
                invitation and to the data flows involved.
              </li>
              <li>
                Do not use the Family feature to obtain or share health data about a person without
                their knowledge and consent.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">9. Your content and our licence</h2>
            <p>
              You retain ownership of all data you enter into the App ("
              <strong>Your Content</strong>"), including biomarker values, lab-report uploads, chat
              messages, notes, and preferences.
            </p>
            <p>
              By using the App, you grant us a limited, worldwide, royalty-free licence to host,
              process, and transmit Your Content{' '}
              <strong>solely for the purpose of operating the App for you</strong> and of providing
              the features you use (for example, sending messages to the AI assistant at your
              instruction, or sharing data with family members you authorise). We do not use Your
              Content to train AI models or for advertising.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">10. Our intellectual property</h2>
            <p>
              The App, including all software, designs, logos, text, graphics, and compiled
              health-score algorithms, is owned by Markur Health Limited or its licensors and is
              protected by intellectual-property laws. You receive a limited, non-exclusive,
              non-transferable, revocable licence to use the App for personal, non-commercial use,
              subject to these Terms.
            </p>
            <p>
              You may not copy, modify, distribute, sell, lease, or create derivative works of the
              App.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">11. Fees</h2>
            <p>
              The App is currently provided <strong>free of charge</strong>. If we introduce paid
              features in future, we will give you advance notice and the chance to accept new terms
              before any charge applies.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">12. Changes to the App</h2>
            <p>
              We may update, change, or discontinue features of the App at any time. We may release
              new versions via Google Play; you may need to install updates to continue using the
              App. We will give reasonable advance notice of material changes that adversely affect
              you where practical.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">13. Ending your use of the App</h2>

            <h3 className="text-xl font-semibold">By you</h3>
            <p>
              You may stop using the App at any time. You can delete your account in{' '}
              <strong>Settings &rarr; Account &rarr; Delete Account</strong>. Deletion performs a{' '}
              <strong>hard deletion</strong> of your personal data in line with our Privacy Policy.
              Deletion is not reversible.
            </p>

            <h3 className="text-xl font-semibold">By us</h3>
            <p>We may suspend or terminate your access if:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>You materially breach these Terms;</li>
              <li>We are required to do so by law;</li>
              <li>Continued operation of the App for you poses a security or legal risk.</li>
            </ul>
            <p>
              Where reasonably possible we will give you advance notice and the opportunity to
              export your data before termination.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">14. Disclaimers</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                The App is provided <strong>"as is" and "as available"</strong>, without warranties
                of any kind, whether express or implied, including fitness for a particular purpose,
                merchantability, accuracy, or non-infringement.
              </li>
              <li>
                We do not warrant that the App will be uninterrupted, error-free, or secure, or that
                insights or biomarker extractions will be accurate.
              </li>
              <li>
                <strong>Health information in the App is not medical advice.</strong> See Section 2.
              </li>
            </ul>
            <p>
              Nothing in these Terms excludes or limits liability for death or personal injury
              caused by negligence, for fraud or fraudulent misrepresentation, or for any liability
              that cannot be excluded or limited under Irish law.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">15. Limitation of liability</h2>
            <p>Subject to Section 14:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                We will not be liable for indirect, incidental, special, consequential, or punitive
                damages, or for loss of profits, data, goodwill, or opportunity.
              </li>
              <li>
                Our total aggregate liability arising out of or related to your use of the App,
                whether in contract, tort, or otherwise, will not exceed{' '}
                <strong>one hundred euro (&euro;100)</strong> or, where the App is provided free of
                charge, the amount you have paid us in the twelve months preceding the event giving
                rise to the claim, <strong>whichever is greater</strong>.
              </li>
            </ul>
            <p>This limitation applies to the fullest extent permitted by law.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">16. Your consumer rights</h2>
            <p>
              If you are a consumer resident in the European Union or United Kingdom, you have
              statutory rights that cannot be waived by contract. Nothing in these Terms limits or
              excludes those rights.
            </p>
            <p>
              EU consumers may use the European Commission's Online Dispute Resolution platform at{' '}
              <a
                href="https://ec.europa.eu/consumers/odr"
                className="font-medium underline"
                target="_blank"
                rel="noreferrer"
              >
                ec.europa.eu/consumers/odr
              </a>
              . We prefer to resolve disputes directly; please contact us at{' '}
              <strong>support@markurhealth.com</strong> first.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">17. Indemnity</h2>
            <p>
              You agree to indemnify and hold harmless Markur Health Limited and its directors,
              officers, and employees from any claims, losses, liabilities, and reasonable expenses
              (including legal fees) arising out of:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Your breach of these Terms;</li>
              <li>Your misuse of the App;</li>
              <li>
                Your violation of another person's rights, including through the Family feature.
              </li>
            </ul>
            <p>
              This Section does not apply to matters for which we are not permitted by law to seek
              indemnity.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">18. Governing law and jurisdiction</h2>
            <p>
              These Terms and any dispute arising out of them are governed by the{' '}
              <strong>laws of Ireland</strong>.
            </p>
            <p>
              The courts of <strong>Ireland</strong> have exclusive jurisdiction to settle any
              dispute, except that consumers resident elsewhere in the EU retain the right to bring
              proceedings in the courts of their habitual residence under Regulation (EU) 1215/2012.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">19. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. When we make material changes we will
              notify you in the App and by email before the changes take effect. Continued use of
              the App after the effective date of the new Terms constitutes your acceptance. If you
              do not agree, you may delete your account.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">20. Miscellaneous</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Entire agreement.</strong> These Terms and the Privacy Policy are the entire
                agreement between you and us regarding the App.
              </li>
              <li>
                <strong>Severability.</strong> If any provision is held unenforceable, the rest
                remains in effect.
              </li>
              <li>
                <strong>No waiver.</strong> Our failure to enforce a right is not a waiver.
              </li>
              <li>
                <strong>Assignment.</strong> You may not assign these Terms. We may assign them to
                an affiliate or successor in connection with a merger, acquisition, or
                reorganisation.
              </li>
              <li>
                <strong>Notices.</strong> We will send notices to the email address on your account.
                You may send notices to <strong>support@markurhealth.com</strong>.
              </li>
              <li>
                <strong>Language.</strong> These Terms are drafted in English. If we provide a
                translation for convenience, the English version prevails in the event of any
                inconsistency.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">21. Contact</h2>
            <p className="text-muted-foreground">
              <strong>Markur Health Limited</strong>
              <br />
              Apartment 610, Domville Crescent, Cherrywood
              <br />
              Dublin 18, D18 WRW7, Ireland
              <br />
              Email:{' '}
              <a href="mailto:support@markurhealth.com" className="font-medium underline">
                support@markurhealth.com
              </a>
            </p>
          </section>
        </div>
      </article>
    </main>
  )
}
